import { CommonModule } from '@angular/common';
import { Component, Renderer2, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ValidationConstant } from '../../../constants/validation.constant';
import {
  Exception,
  LaunchDarklyService,
  StorageService,
  StorageType,
  ToasterService,
  Validator,
} from '@anddone/ui-utilities';
import { ActivatedRoute, Router } from '@angular/router';
import { AppSetting } from '../../../constants/appsetting.constant';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { LoginService } from '../../../services/login-service';
import { Subscription } from 'rxjs';
import { MessageConstants } from '../../../constants/message.constant';

@Component({
  selector: 'app-change-password',
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, RecaptchaV3Module],
  templateUrl: './change-password.html',
  styleUrl: './change-password.scss',
})
export class ChangePassword implements OnInit, OnDestroy {
  changePasswordForm!: FormGroup;
  changePasswordFormErrors: any = {};
  isLoader = signal<boolean>(false);
  validator: Validator;
  subscription!: Subscription;
  newPassFieldType = 'password';
  confirmPassFieldType = 'password';
  routeParameter: any;
  allyId = '';
  userName = '';

  featureFlags: any;

  currentYear: number = new Date().getFullYear();
  appSetting = AppSetting;

  config = {
    newPassword: {
      required: { name: ValidationConstant.changePassword.newPassword.name },
      maxlength: {
        name: ValidationConstant.changePassword.newPassword.name,
        max: ValidationConstant.changePassword.newPassword.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.changePassword.newPassword.name,
        min: ValidationConstant.changePassword.newPassword.minLength.toString(),
      },
      pattern: { name: ValidationConstant.changePassword.newPassword.name },
    },
    confirmPassword: {
      required: { name: ValidationConstant.changePassword.confirmPassword.name },
      maxlength: {
        name: ValidationConstant.changePassword.confirmPassword.name,
        max: ValidationConstant.changePassword.newPassword.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.changePassword.confirmPassword.name,
        min: ValidationConstant.changePassword.confirmPassword.minLength.toString(),
      },
      pattern: { name: ValidationConstant.changePassword.confirmPassword.name },
    },
  };
  private formBuilder = inject(FormBuilder);
  private loginService = inject(LoginService);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private activateRoute = inject(ActivatedRoute);
  private toasterService = inject(ToasterService);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private renderer = inject(Renderer2);
  private launchDarklyService = inject(LaunchDarklyService);
  constructor() {
    this.validator = new Validator(this.config);
  }

  ngOnInit(): void {
    this.routeParameter = this.activateRoute.params.subscribe((params) => {
      this.allyId = params['allyId'];
      this.userName = params['username'];
    });

    this.changePasswordForm = this.formBuilder.group({
      newPassword: [
        '',
        [
          Validators.required,
          Validators.maxLength(ValidationConstant.changePassword.newPassword.maxLength),
          Validators.minLength(ValidationConstant.changePassword.newPassword.minLength),
          Validators.pattern(ValidationConstant.passwordRegex),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.maxLength(ValidationConstant.changePassword.confirmPassword.maxLength),
          Validators.minLength(ValidationConstant.changePassword.confirmPassword.minLength),
          Validators.pattern(ValidationConstant.passwordRegex),
        ],
      ],
    });

    this.changePasswordForm.valueChanges.subscribe((data) => this.onValueChanged(data));

    this.renderer.addClass(document.body, 'recaptcha');

    // Get all feature flags for the project and subscribe to changes
    this.featureFlags = this.launchDarklyService.getAllFeatureFlags(
      AppSetting.launchDarklyProjectName
    );
    this.launchDarklyService.LD_emitters[AppSetting.launchDarklyProjectName]?.subscribe(
      (allFlags: any) => {
        this.featureFlags = allFlags;
      }
    );
  }

  // onValueChanged method is used to validate the form fields and set the error messages in changePasswordFormErrors object
  onValueChanged(_data?: any) {
    if (!this.changePasswordForm) {
      return;
    }
    this.changePasswordFormErrors = this.validator.validate(this.changePasswordForm);
  }

  // get recaptcha token
  executeRecaptcha() {
    this.subscription = this.recaptchaV3Service
      .execute('resetPassword')
      .subscribe((token) => this.changePassword(token));
  }

  changePassword(token: any) {
    this.validator.validateAllFormFields(this.changePasswordForm);
    this.changePasswordFormErrors = this.validator.validate(this.changePasswordForm);
    if (this.changePasswordForm?.invalid) {
      return;
    }

    if (
      this.changePasswordForm?.controls['newPassword'].value !=
      this.changePasswordForm?.controls['confirmPassword'].value
    ) {
      this.toasterService.showError(MessageConstants.changePassword.errorPasswordMismatch, 'full');
      this.isLoader.set(false);
      return;
    }

    this.isLoader.set(true);
    const data = this.storageService.get(StorageType.Session, 'userOldPwd');
    const oldPassword = data ? JSON.parse(data) : null;
    const reqObj = {
      userName: this.userName,
      allyId: this.allyId,
      data: {
        oldPassword: oldPassword,
        newPassword: this.changePasswordForm?.controls['newPassword'].value,
        isReset: false,
        CaptchaToken: token,
        ActionName: 'resetPassword',
      },
    };

    if (this.featureFlags?.suppress_recaptcha_validations_reset_password) {
      reqObj.data.CaptchaToken = null;
    }

    this.loginService.changePassword(reqObj)?.subscribe({
      next: (res: any) => {
        res.userName = this.userName;
        this.storageService.save(StorageType.Session, 'resetPwdUser', JSON.stringify(res));
        this.toasterService.showSuccess(
          MessageConstants.changePassword.changePasswordSuccessMessage,
          'full'
        );
        this.router.navigate(['/login']);
        this.isLoader.set(false);
      },
      error: (e) => {
        this.changePasswordForm.reset();
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  onTogglePassword(fieldType: string) {
    if (fieldType === 'newPass') {
      this.newPassFieldType = this.newPassFieldType === 'password' ? 'text' : 'password';
    } else {
      this.confirmPassFieldType = this.confirmPassFieldType === 'password' ? 'text' : 'password';
    }
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'recaptcha');
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
